

import React, { memo } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 15, 20, 25, 30, 50];

function DataTablePaginationComponent({
  currentPage,
  onChangePage,
  onChangeRowsPerPage,
  rowCount,
  rowsPerPage,
  paginationRowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
}) {
  const { t } = useTranslation(["pagination", "common"]);
  const paginationOptions = {
    perPage: rowsPerPage,
    pageNumber: currentPage,
    totalItems: rowCount,
  };

  const generatePaginationNumbers = () => {
    const { perPage, pageNumber, totalItems } = paginationOptions;
    const totalPages = Math.ceil(totalItems / perPage);

    if (totalPages === 0) return [];

    const startPage = Math.max(1, pageNumber - 1);
    const endPage = Math.min(totalPages, pageNumber + 1);
    const firstIndex = 1;
    const lastIndex = totalPages;

    const paginationNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      paginationNumbers.push(i);
    }

    if (firstIndex + 1 < endPage && !paginationNumbers.includes(firstIndex + 1))
      paginationNumbers.unshift("...");
    if (!paginationNumbers.includes(firstIndex)) paginationNumbers.unshift(1);

    if (lastIndex - 1 > startPage && !paginationNumbers.includes(lastIndex - 1))
      paginationNumbers.push("...");
    if (!paginationNumbers.includes(lastIndex))
      paginationNumbers.push(totalPages);

    return paginationNumbers;
  };

  return (
    <div className="dataTablePagination">
      <style>{`
        .dataTablePagination {
          container-type: inline-size;
          container-name: dataTablePagination;
        }
        .dataTablePagination .show {
          display: inline;
        }
        @container dataTablePagination (max-width: 450px) {
          .dataTablePagination .show {
            display: none !important;
          }
        }
      `}</style>

      <div className="d-flex gap-1">
        <p className="show">
          {t("rowsPerPage", { ns: "pagination", defaultValue: "Rows per page" })}
        </p>
        <select
          className="perPage"
          onChange={(e) => onChangeRowsPerPage(+e.target.value)}
          value={rowsPerPage}
        >
          {paginationRowsPerPageOptions.map((rows) => (
            <option value={rows} key={rows}>
              {rows}
            </option>
          ))}
        </select>
      </div>

      <p>
        <span className="show">
          {t("showing", { ns: "pagination", defaultValue: "Rows" })}{" "}
        </span>
        {Math.max(0, (currentPage - 1) * rowsPerPage + 1)}-
        {Math.min(rowCount, currentPage * rowsPerPage)}{" "}
        {t("of", { ns: "pagination", defaultValue: "of" })} {rowCount}
      </p>

      <div className="buttons">
        <button
          className="btn"
          disabled={currentPage == 1 || currentPage == 0}
          onClick={() => onChangePage(currentPage - 1)}
        >
          {t("prev", { ns: "pagination", defaultValue: "Prev" })}
        </button>
        {generatePaginationNumbers().map((num, index) => {
          if (typeof num === "string")
            return (
              <p className="show" key={num + index}>
                {num}
              </p>
            );
          return (
            <button
              className={
                "btn show" +
                (paginationOptions.pageNumber === num ? " selected" : "")
              }
              key={num}
              onClick={() => onChangePage(num)}
            >
              {num}
            </button>
          );
        })}
        <button
          className="btn"
          disabled={currentPage === Math.ceil(rowCount / rowsPerPage)}
          onClick={() => onChangePage(currentPage + 1)}
        >
          {t("next", { ns: "pagination", defaultValue: "Next" })}
        </button>
      </div>
    </div>
  );
}

export default memo(DataTablePaginationComponent);
